import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

/**
 * Extend-expiry dialog: days (1-60) + a required reason. Modeled on the shared
 * InputDialog markup, extended to two fields (InputDialog only supports one).
 */
const ExtendDialog = ({ isOpen, onClose, onSubmit, loading = false, vendorName, stageLabel }) => {
  const [days, setDays] = useState('7');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setDays('7');
      setReason('');
      setErrors({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !loading) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading, onClose]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    const next = {};
    const daysNum = parseInt(days, 10);
    if (!daysNum || daysNum < 1 || daysNum > 60) {
      next.days = 'Enter a number of days between 1 and 60';
    }
    if (!reason.trim() || reason.trim().length < 3) {
      next.reason = 'A reason of at least 3 characters is required';
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    onSubmit({ days: daysNum, reason: reason.trim() });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={loading ? undefined : onClose}
      />
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-start gap-4 mb-4">
          <Icon name="CalendarClock" size={24} className="text-blue-600" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Extend Link Expiry</h3>
            <p className="text-sm text-gray-600">
              Extend the {stageLabel} link for{' '}
              <span className="font-medium">{vendorName}</span>. The days are added to the expiry it
              already has (or to today, if it has already lapsed), so extending never shortens a
              link. The underlying link's expiry is updated too.
            </p>
          </div>
        </div>

        <div className="mb-4">
          <Input
            type="number"
            label="Extend by (days)"
            min={1}
            max={60}
            value={days}
            onChange={(e) => {
              setDays(e.target.value);
              setErrors((prev) => ({ ...prev, days: undefined }));
            }}
            error={errors.days}
            required
          />
        </div>
        <div className="mb-4">
          <Input
            label="Reason"
            placeholder="Why is this being extended?"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setErrors((prev) => ({ ...prev, reason: undefined }));
            }}
            error={errors.reason}
            required
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !loading) handleSubmit();
            }}
          />
        </div>

        <div className="flex gap-3">
          <Button onClick={onClose} variant="outline" className="flex-1" disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} className="flex-1" loading={loading} disabled={loading}>
            Extend
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExtendDialog;
